import { isPartner } from '../../../helpers/appType';
import {
  ConversationSide,
  ConversationType,
} from '../../../types/conversation.types';

/** What a `message_read` event is worth, which depends on the thread. */

export type ReadReceiptEffect =
  /** Says nothing about our ticks - our own catching up, or unusable. */
  | 'ignore'
  /** Everything of ours up to the marker has been read. */
  | 'markSeen'
  /** Only the server can answer; re-read `seen` from it. */
  | 'refresh';

/**
 * Whose reading a receipt describes, and therefore what it proves.
 *
 * The event is the same shape everywhere and means three different things:
 *
 *   - `support` - the thread has two ends, vGrubs and one outside user, and
 *     `side` names the one that read. The vGrubs end reads as a COLLECTIVE, so
 *     no user id is involved (nor sent - `readerUserId` is null here), and a
 *     receipt from our own side is our own tab catching up.
 *   - `direct` - two internal users, so `side` is `internal` for both of them
 *     and answers nothing. `readerUserId` is what separates them, and once it
 *     is not us it is the only other person there: their marker IS the tick.
 *   - `group` / `merchantGroup` - the tick is all-or-nothing across every
 *     other member who was in the thread when the message was sent - on both
 *     apps, since a merchant is a member like any other there and their
 *     receipt carries `readerUserId` with a null `side`. One member's marker
 *     cannot answer that. Neither can this tab, which only ever learns the
 *     markers that MOVE while it is open: a colleague who read yesterday and
 *     says nothing today would hold every tick down forever. The backend
 *     computes the whole condition on each read, so it is asked rather than
 *     guessed at.
 *
 * A receipt with no `readerUserId` at all is a gateway from before 2026-08.
 * There the internal case is unusable rather than wrong - `side` is `internal`
 * for the reader and for us alike - so it falls in with `support` and is
 * dropped exactly as it always was, leaving the ticks to the next fetch.
 *
 * An unknown `type` lands on `refresh`, which is the safe side of that guess:
 * correct for a direct too, only costlier.
 */
export const readReceiptEffect = ({
  type,
  side,
  readerUserId,
  viewerId,
}: {
  type: ConversationType | undefined;
  side: ConversationSide | null;
  readerUserId: number | null | undefined;
  viewerId: number | null;
}): ReadReceiptEffect => {
  if (readerUserId == null || type === 'support') {
    return side === (isPartner ? 'user' : 'internal') ? 'ignore' : 'markSeen';
  }
  // The room gets every receipt back, our own included.
  if (readerUserId === viewerId) return 'ignore';
  return type === 'direct' ? 'markSeen' : 'refresh';
};
